import {
  faAngleLeft,
  faAngleRight,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addDays,
  isEqual,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  getWeek,
  max,
} from "date-fns";

import { de } from "date-fns/locale";
import { range, round, sum, toPairs, flatten } from "lodash";
import React, { useMemo } from "react";

import { TimesheetEntry } from "../../core/types";
import { formatDe } from "../../utils/date";
import styled from "./calendar.styles";
import { Badge } from "reactstrap";

interface CalendarHeaderProps {
  timesheetEntries: TimesheetEntry[];
  currentMonth: Date;
  onPrev(): void;
  onNext(): void;
}

interface CalendarDaysProps {
  currentMonth: Date;
  onPrev(): void;
  onNext(): void;
}

const CalendarDays: React.FC<CalendarDaysProps> = (props) => {
  const { currentMonth } = props;
  const dateFormat = "EEEEEE";

  const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 });

  return (
    <styled.CalendarDays>
      <div style={{ background: "none", border: "none" }}>&nbsp;</div>
      {range(0, 7).map((i) => (
        <div key={i}>{formatDe(addDays(startDate, i), dateFormat)}</div>
      ))}
      <div>&nbsp;</div>
    </styled.CalendarDays>
  );
};

interface CalendarCellsProps {
  currentMonth: Date;
  selectedDate: Date;
  timesheetEntries: TimesheetEntry[];
  currentWeekData: Array<[string, Date[]]>;
  onSelectDate(date: Date): void;
}

const CalendarCells: React.FC<CalendarCellsProps> = (props) => {
  const {
    currentWeekData,
    currentMonth,
    timesheetEntries,
    selectedDate,
    onSelectDate,
  } = props;
  const monthStart = startOfMonth(currentMonth);
  const today = new Date(Date.now());

  const dateFormat = "dd.";

  const days = currentWeekData.map(([week, days]) => (
    <React.Fragment key={week}>
      <div className="firstDayOfWeek" key={`col0_${week}`}>
        <div style={{ position: "relative", display: "flex" }}>
          <FontAwesomeIcon
            icon={faCalendar}
            className="text-secondary"
            style={{ fontSize: 30 }}
          />
          <span>
            {formatDe(max([days[0], startOfMonth(currentMonth)]), dateFormat)}
          </span>
        </div>
      </div>
      {days.map((day) => {
        const entries = timesheetEntries.filter((e) =>
          isEqual(startOfDay(e.start), startOfDay(day))
        );

        const states: Record<string, (date: Date) => boolean> = {
          disabled: (day) => !isSameMonth(day, monthStart),
          selected: (day) => isSameDay(day, selectedDate),
          today: (day) => isSameDay(day, today),
        };

        const activeStates = toPairs(states)
          .filter(([_, predicate]) => predicate(day))
          .map(([state]) => state);

        return (
          <div key={day.getTime()}>
            <div
              className={`day ${activeStates.join(" ")}`}
              onClick={() =>
                !activeStates.includes("disabled") && onSelectDate(day)
              }
            >
              <span className="workedHours">
                {entries.length ? getWorkedHours(entries) : <>&nbsp;</>}
              </span>
            </div>
          </div>
        );
      })}
      <div className="sumOfWeek" key={`sumOfWeek_${week}`}>
        <div>
          <Badge pill color="info">
            {Number(
              getWorkedHours(
                timesheetEntries.filter(
                  (e) =>
                    getWeek(e.start, {
                      weekStartsOn: 1,
                      locale: de,
                    }) === Number(week)
                )
              )
            )}
            h
          </Badge>
        </div>
      </div>
    </React.Fragment>
  ));

  return <styled.CalendarCells>{flatten(days)}</styled.CalendarCells>;
};

interface CalendarProps {
  timesheetEntries: TimesheetEntry[];
  currentWeekData: Array<[string, Date[]]>;
  selectedDate: Date;
  onPrevMonth(): void;
  onNextMonth(): void;
  onSelectedDate(date: Date): void;
}

const Calendar: React.FC<CalendarProps> = (props) => {
  const {
    timesheetEntries,
    currentWeekData,
    onNextMonth: nextMonth,
    onPrevMonth: prevMonth,
    onSelectedDate: setSelectedDate,
    selectedDate,
  } = props;

  const currentMonth = useMemo(
    () => startOfMonth(selectedDate),
    [selectedDate]
  );

  return (
    <div className="calendar">
      <div id="calendarHeader">
        <CalendarHeader
          timesheetEntries={timesheetEntries}
          onNext={nextMonth}
          onPrev={prevMonth}
          currentMonth={currentMonth}
        />
      </div>
      <CalendarDays
        onNext={nextMonth}
        onPrev={prevMonth}
        currentMonth={currentMonth}
      />
      <CalendarCells
        currentWeekData={currentWeekData}
        timesheetEntries={timesheetEntries}
        currentMonth={currentMonth}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
      />
    </div>
  );
};

const CalendarHeader: React.FC<CalendarHeaderProps> = (props) => {
  const { timesheetEntries, onPrev, onNext, currentMonth } = props;
  return (
    <styled.CalendarHeader>
      <h4>
        <div className="icon iconPrev" onClick={onPrev}>
          <FontAwesomeIcon icon={faAngleLeft} />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className="monthTitle">
            {formatDe(currentMonth, "MMMM yyyy")}{" "}
          </span>
          <Badge pill color="info" style={{ marginLeft: "0.5rem" }}>
            <MonthTotal
              timesheetEntries={timesheetEntries}
              currentMonth={currentMonth}
            />
          </Badge>
        </div>
        <div className="icon iconNext" onClick={onNext}>
          <FontAwesomeIcon icon={faAngleRight} />
        </div>
      </h4>
    </styled.CalendarHeader>
  );
};

const MonthTotal: React.FC<{
  timesheetEntries: TimesheetEntry[];
  currentMonth: Date;
}> = (props) => {
  const { timesheetEntries, currentMonth } = props;

  return (
    <>
      {getWorkedHours(
        timesheetEntries.filter(
          (e) => e.start.getMonth() === currentMonth.getMonth()
        )
      ) + "h"}
    </>
  );
};

function getWorkedHours(entries: TimesheetEntry[]) {
  return round(sum(entries.map((e) => e.duration)) / 3600000, 1);
}

export default Calendar;
